import { type CurrencyCode, type Locale } from '@transfers-system/contracts';
import { Decimal } from 'decimal.js';

/**
 * Display formatting. Values arrive from the API as decimal strings and are
 * only ever converted to a number at the very last step, inside Intl, so no
 * arithmetic is ever done in floating point.
 */

/** Latin digits everywhere: an exchange's numbers must be unambiguous. */
const NUMBERING = 'latn';

export function formatAmount(
  value: string,
  locale: Locale,
  options: { minimumFractionDigits?: number; maximumFractionDigits?: number } = {},
): string {
  const decimal = new Decimal(value);
  return new Intl.NumberFormat(`${locale}-u-nu-${NUMBERING}`, {
    minimumFractionDigits: options.minimumFractionDigits ?? 2,
    maximumFractionDigits: options.maximumFractionDigits ?? 2,
  }).format(decimal.toNumber());
}

export function formatCurrency(
  value: string,
  currency: CurrencyCode,
  locale: Locale,
  fractionDigits = 2,
): string {
  return new Intl.NumberFormat(`${locale}-u-nu-${NUMBERING}`, {
    style: 'currency',
    currency,
    minimumFractionDigits: fractionDigits,
    maximumFractionDigits: fractionDigits,
  }).format(new Decimal(value).toNumber());
}

/** Rates need more decimals than money, and never fewer. */
export function formatRate(value: string, locale: Locale, fractionDigits = 4): string {
  return new Intl.NumberFormat(`${locale}-u-nu-${NUMBERING}`, {
    minimumFractionDigits: fractionDigits,
    maximumFractionDigits: fractionDigits,
  }).format(new Decimal(value).toNumber());
}

/**
 * The organisation's display timezone. Every business timestamp renders in it,
 * never in the browser's own zone. It mirrors the API's `ORGANIZATION_TIMEZONE`
 * default; the web app has no runtime source for it.
 */
export const DISPLAY_TIMEZONE = 'Asia/Amman';

/**
 * ui-contract §7: dates render `dd/MM/yyyy`, date-times `dd/MM/yyyy hh:mm AM`
 * or `PM` - one fixed, unambiguous, Latin-digit, 12-hour format regardless of
 * locale (`AM`/`PM` stay Latin in Arabic too). `Intl`'s `dateStyle`/`timeStyle`
 * presets are locale-shaped (reordered fields, Arabic digits, ص/م), so this asks
 * `Intl` only for the zone-converted parts and assembles the string itself.
 * `locale` stays in the signature for call-site stability; every render site
 * also wraps the value in its own `dir="ltr"` isolate so it is never reordered
 * inside a surrounding Arabic table cell.
 */
function twelveHourParts(date: Date, timezone: string): Record<string, string> {
  const parts = new Intl.DateTimeFormat(`en-US-u-nu-${NUMBERING}`, {
    timeZone: timezone,
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h12',
  }).formatToParts(date);
  return Object.fromEntries(parts.map((part) => [part.type, part.value]));
}

function toDate(value: string | Date): Date {
  return value instanceof Date ? value : new Date(value);
}

function assembleDate(p: Record<string, string>): string {
  return `${p['day']}/${p['month']}/${p['year']}`;
}

function assembleTime(p: Record<string, string>): string {
  return `${p['hour']}:${p['minute']} ${p['dayPeriod']?.toUpperCase()}`;
}

/** Keeps a Latin value reading left-to-right inside a surrounding RTL sentence. */
export function ltrIsolate(value: string): string {
  return `\u2066${value}\u2069`;
}

/** `dd/MM/yyyy hh:mm AM|PM` in `timezone` (default: the organisation's). */
export function formatZonedDateTime(
  value: string | Date,
  _locale: string,
  timezone: string = DISPLAY_TIMEZONE,
): string {
  const p = twelveHourParts(toDate(value), timezone);
  return `${assembleDate(p)} ${assembleTime(p)}`;
}

export function formatDateTime(value: string | Date, locale: Locale): string {
  return formatZonedDateTime(value, locale, DISPLAY_TIMEZONE);
}

/** `hh:mm AM|PM` in the organisation's timezone. */
export function formatTime(value: string | Date, _locale: string = 'ar'): string {
  return assembleTime(twelveHourParts(toDate(value), DISPLAY_TIMEZONE));
}

/** `dd/MM/yyyy` of the instant's calendar day in the organisation's timezone. */
export function formatDate(value: string | Date, _locale: Locale): string {
  return assembleDate(twelveHourParts(toDate(value), DISPLAY_TIMEZONE));
}

/**
 * Rule N2: this module is the only place that constructs an `Intl` formatter.
 * The workspace screens grew their own parallel implementations; they now
 * delegate here so there is one numbering, one calendar and one place to audit.
 */

/** Counts and quantities in the active language, always with Latin digits. */
export function formatCount(value: number, locale: string): string {
  return new Intl.NumberFormat(`${locale}-u-nu-${NUMBERING}`).format(value);
}

/** A region name in the active language, for country pickers and labels. */
export function formatRegionName(code: string, locale: string): string {
  return new Intl.DisplayNames([locale], { type: 'region' }).of(code) ?? code;
}

/**
 * Splits an instant into fixed-width calendar parts in a named timezone, for
 * building a query boundary — not for display. The numbering subtag is not
 * cosmetic here: a runtime defaulting to Arabic-Indic digits would produce
 * `٢٠٢٦` and silently corrupt the assembled string.
 */
export function zonedDateParts(
  value: Date,
  timezone: string,
): Record<Intl.DateTimeFormatPartTypes, string> {
  const parts = new Intl.DateTimeFormat(`en-GB-u-nu-${NUMBERING}`, {
    timeZone: timezone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hourCycle: 'h23',
  }).formatToParts(value);
  return Object.fromEntries(parts.map((part) => [part.type, part.value])) as Record<
    Intl.DateTimeFormatPartTypes,
    string
  >;
}

/**
 * The employee of a record in the current language (T1): the other language
 * when one is missing, and "—" when the employee is unknown. Never an id.
 */
export function employeeName(
  employee: { nameAr: string; nameEn: string } | null | undefined,
  locale: string,
): string {
  if (!employee) return '—';
  const first = locale === 'ar' ? employee.nameAr : employee.nameEn;
  const second = locale === 'ar' ? employee.nameEn : employee.nameAr;
  return first || second || '—';
}
